import { ActionIcon, Textarea } from '@mantine/core';
import React, { useCallback, useEffect, useState } from 'react';
import { FileTile } from 'src/components/Chat/FileTile';
import FileUploadModal from 'src/components/Modals/FileUpload';
import UpgradePlanModal from 'src/components/Modals/UpgradePlanModal';
import { UserRole } from 'src/enums/User';
import { useRealtimeTranscription } from 'src/hooks/useRealtimeTranscription';
import { useAppSelector } from 'src/redux/redux-store/hooks';
import { COLORS } from 'src/utils/styles';
import SpeechModal from '../../SpeechModal/SpeechModal';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import Icon from '../Icons/Icons/Icon';
import AskQuestionButton from './AskQuestionButton';

interface ChatInputProps {
  inputMessage: string;
  setInputMessage: React.Dispatch<React.SetStateAction<string>>;
  onSend: (message: string) => void;
  uploadedFile: File | null;
  setUploadedFile: React.Dispatch<React.SetStateAction<File | null>>;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  streaming: boolean;
  handleUpgrade: () => void;
  access: 'Free' | 'Premium';
  disabled: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputMessage,
  setInputMessage,
  onSend,
  uploadedFile,
  setUploadedFile,
  handleUpgrade,
  access,
  uploadStatus,
  streaming,
  disabled,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [speechModalOpen, setSpeechModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = React.useState(false);
  const role = useAppSelector((state) => state.user.role) ?? UserRole.user;
  const isChild = role === UserRole.user;

  // Handle real-time transcription
  const handleTranscription = useCallback(
    (text: string, isFinal: boolean) => {
      setInputMessage((prev) => {
        // Get the base text (without any listening indicator)
        // const baseText = prev.includes("(Listening...)")
        //   ? prev.split(" (Listening...)")[0].trim()
        //   : prev.trim();
        const baseText = prev;

        if (isFinal) {
          // For final transcriptions, append to the existing message with proper spacing
          if (!baseText) return text; // If no previous text, just use the new text

          // If the previous message doesn't end with punctuation, add a space before appending
          const separator = /[.!?,;:]\s*$/.test(baseText) ? ' ' : ' ';
          return `${baseText}${separator}${text}`.trim();
        } else {
          // For partial transcriptions, show with listening indicator
          const displayText = baseText ? `${baseText} ${text}` : text;
          return `${displayText}`;
        }
      });
    },
    [inputMessage, setInputMessage]
  );

  const {
    isRecording,
    isTranscribing,
    isRtcConnected,
    error,
    startListening,
    stopListening,
    resetError,
    resetTranscription,
  } = useRealtimeTranscription(handleTranscription, {
    language: 'en',
    enableVad: true,
  });

  // State for error message
  const [showError, setShowError] = useState<string | null>(null);

  // Handle transcription errors
  useEffect(() => {
    if (error) {
      console.error('Transcription error:', error);

      // Check for permission denied error specifically
      const isPermissionError =
        error.type === 'permission_denied' ||
        error.message.toLowerCase().includes('permission') ||
        error.message.toLowerCase().includes('denied');

      // Set appropriate error message
      const errorMessage = isPermissionError
        ? 'Microphone access was denied. Please allow microphone access in your browser settings and try again.'
        : error.message || 'An error occurred during speech recognition';

      setShowError(errorMessage);

      // Reset transcription states on error
      resetTranscription();

      // Clear any listening indicators from input
      setInputMessage((prev) =>
        prev.replace(/\s*\(Listening\.\.\.\)\s*$/, '').trim()
      );

      // Auto-clear error after 3 seconds
      const timer = setTimeout(() => {
        setShowError(null);
        resetError();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [error, resetError, resetTranscription, setInputMessage]);

  const handleSend = useCallback(() => {
    if (!disabled && inputMessage.trim()) {
      onSend(inputMessage.trim());
      setInputMessage('');
    }
  }, [disabled, inputMessage, onSend, setInputMessage]);

  const toggleSpeechToText = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      if (isRecording) {
        // Clean up the input message by removing the "(Listening...)" text if present
        const cleanedMessage = inputMessage
          .replace(/\(Listening\.\.\.\)\s*/, '')
          .trim();
        setInputMessage(cleanedMessage);

        // Stop recording
        await stopListening();
      } else {
        // Reset any previous transcription state
        resetTranscription();

        // Clear any existing error
        resetError();
        setShowError(null);

        // Clear the input if it only contains the listening indicator or error message
        if (
          // inputMessage.includes("(Listening...)") ||
          inputMessage.includes('(Error:')
        ) {
          setInputMessage('');
        }

        // Start recording
        await startListening();
      }
    } catch (err) {
      console.error('Error in speech-to-text:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Could not access microphone';
      const baseText = inputMessage.split(
        /\s*\(Listening\.\.\.\)|\s*\(Error:.*\)/
      )[0];
      setInputMessage(`${baseText} (Error: ${errorMessage})`.trim());

      // Reset transcription state on error
      resetTranscription();
    }
  };

  // Handle recording state changes
  // Debug log state changes
  useEffect(() => {
    console.log('State update:', {
      isRecording,
      isTranscribing,
      isRtcConnected,
    });
  }, [isRecording, isTranscribing, isRtcConnected]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isRecording) {
        e.preventDefault();
        stopListening();
      }
    };

    if (isRecording) {
      // Add keyboard event listener for stopping with Escape key
      window.addEventListener('keydown', handleKeyDown);

      // Only add the listening indicator if it's not already there
      // if (!inputMessage.includes("(Listening...)")) {
      //   const baseText = inputMessage.split(" (Listening...)")[0];
      //   setInputMessage(`${baseText} (Listening...)`);
      // }
    } else {
      // Remove keyboard event listener when not recording
      window.removeEventListener('keydown', handleKeyDown);

      // Only remove the listening indicator if it's there
      // if (inputMessage.includes("(Listening...)")) {
      //   const baseText = inputMessage.split(" (Listening...)")[0];
      //   setInputMessage(baseText.trim());
      // }
    }

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRecording, inputMessage, setInputMessage, stopListening]);

  return (
    <div className="mb-2 relative">
      {uploadedFile && uploadStatus == 'idle' && (
        <FileTile file={uploadedFile} onRemove={() => setUploadedFile(null)} />
      )}
      {isChild && <AskQuestionButton />}
      <div className="flex items-center flex-row gap-2">
        <Textarea
          value={inputMessage}
          autosize
          size="sm"
          w="100%"
          minRows={1}
          maxRows={2}
          onKeyDown={(e) => {
            console.log('Key pressed:', e.key, e.shiftKey);
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!disabled && !isRecording) {
                handleSend();
              }
            }
          }}
          styles={{
            input: {
              '&:focus': {
                borderColor: COLORS.primary,
              },
              paddingRight: '4.5rem',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              ...(isRecording && {
                borderColor: COLORS.primary,
                boxShadow: `0 0 0 2px ${COLORS.primary}33`,
              }),
            },
            rightSection: {
              marginRight: 10,
            },
          }}
          radius={10}
          onChange={(e) =>
            !isRecording &&
            // !isTranscribing &&
            setInputMessage(e.currentTarget.value)
          }
          placeholder={
            isRecording
              ? 'Speak now (recording in progress)...'
              : // : isTranscribing
                // ? "Processing speech..."
                'Type your message or click the mic to speak'
          }
          disabled={
            streaming || uploadStatus === 'uploading' || isRecording
            // ||
            // isTranscribing
          }
          rightSectionWidth={64}
          rightSection={
            <div className="flex items-center gap-1">
              {/* File upload button */}
              <ActionIcon
                className="upload-files-step"
                variant="light"
                onClick={() => {
                  if (access === 'Free') {
                    setUpgradeModalOpen(true);
                  } else {
                    setIsModalOpen(true);
                  }
                }}
                disabled={
                  isRecording
                  // || isTranscribing
                }
              >
                <Icon name="attach" size="1rem" color="black" />
              </ActionIcon>

              {/* Microphone/Stop button */}
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                {/* Stop button - when RTC is connected and recording */}
                {isRtcConnected && isRecording && (
                  <ActionIcon
                    variant="light"
                    onClick={async (e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await toggleSpeechToText(e);
                    }}
                    className="bg-red-500"
                  >
                    <div className="w-3 h-3 bg-white rounded-sm"></div>
                  </ActionIcon>
                )}

                {/* New mic button opens speech modal */}
                {!speechModalOpen && (
                  <ActionIcon
                    variant="light"
                    className="chat-mic-button"
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSpeechModalOpen(true);
                    }}
                  >
                    <Icon name="mic" size="1rem" color="black" />
                  </ActionIcon>
                )}

                {/* Recording indicator dot */}
                {isRecording && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                )}
              </div>
            </div>
          }
        />
        <button
          type="button"
          className={`bg-primary hover:bg-primary-dark w-8 h-8 rounded-md p-[5px] flex items-center justify-center ${
            disabled || isModalOpen ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={disabled || isModalOpen}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isRecording) {
              handleSend();
            }
          }}
          aria-label="Send message"
        >
          <img src="/icons/send.png" alt="Send" className="w-4 h-4" />
        </button>
      </div>
      <SpeechModal
        opened={speechModalOpen}
        onClose={() => setSpeechModalOpen(false)}
        onComplete={(text) => {
          console.log('SpeechModal onComplete', text);
          onSend(text);
        }}
      />
      <UpgradePlanModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        onUpgrade={handleUpgrade}
      />
      <FileUploadModal
        isOpen={isModalOpen}
        customClasses={{
          modal: 'bg-white',
          title: 'text-xl text-gray-800',
          button: 'bg-blue-500 hover:bg-blue-600 text-white',
          errorText: 'text-red-600 font-bold',
        }}
        onClose={() => setIsModalOpen(false)}
        onFileUpload={(file) => setUploadedFile(file)}
        allowedFileTypes="image/*,application/pdf"
        maxFileSize={10 * 1024 * 1024}
      />

      {showError && (
        <ErrorMessage
          error={showError}
          handleClose={() => {
            setShowError(null);
            resetError();
          }}
        />
      )}
    </div>
  );
};

export default ChatInput;
